/**
 * Auth Entry Point
 * 
 * SINGLE SOURCE OF TRUTH for all authentication in the app.
 * Routes to Emergent or Supabase based on USE_EMERGENT_AUTH flag.
 * 
 * ALL auth operations in the app MUST go through this file.
 * Do NOT import from providers directly.
 */

import { USE_EMERGENT_AUTH, API_URL } from './authConfig';
import * as emergentAuth from './providers/emergentAuth';
import * as supabaseAuth from './providers/supabaseAuth';

// Re-export config for convenience
export { USE_EMERGENT_AUTH, API_URL, AUTH_CALLBACK_PATH, isSupabaseConfigured } from './authConfig';

/**
 * Initiate Google OAuth login
 * Routes to Emergent or Supabase based on feature flag
 * @param {string} redirectPath - Optional redirect path after auth
 * @returns {Promise<{error: Error|null}|void>}
 */
export async function loginWithGoogle(redirectPath = '/dashboard') {
  console.log(`[Auth] loginWithGoogle - using ${USE_EMERGENT_AUTH ? 'Emergent' : 'Supabase'}`);
  
  if (USE_EMERGENT_AUTH) {
    emergentAuth.loginWithGoogle(redirectPath);
    return { error: null };
  }
  
  return await supabaseAuth.loginWithGoogle();
}

/**
 * Handle OAuth callback
 * Call this from the /auth/callback route
 * @returns {Promise<{success: boolean, user: object|null, error: string|null}>}
 */
export async function handleAuthCallback() {
  console.log(`[Auth] handleAuthCallback - using ${USE_EMERGENT_AUTH ? 'Emergent' : 'Supabase'}`);
  
  if (USE_EMERGENT_AUTH) {
    return await emergentAuth.handleAuthCallback();
  }
  
  return await supabaseAuth.handleAuthCallback(API_URL);
}

/**
 * Check if current path is a public route that doesn't need auth
 * @returns {boolean}
 */
function isPublicRoute() {
  const path = window.location.pathname;
  return path.startsWith('/public/') || 
         path.startsWith('/public-') ||
         path.startsWith('/review/') ||
         path.startsWith('/book/') ||
         path.startsWith('/unsubscribe/') ||
         path.startsWith('/activate') ||
         path.startsWith('/reset-password') ||
         path.startsWith('/forgot-password');
}

/**
 * Get current authenticated user
 * @returns {Promise<object|null>} User object or null if not authenticated
 */
export async function getCurrentUser() {
  // CRITICAL: Never call auth endpoints for public routes
  // This prevents 401 errors and body-stream-already-read issues
  if (isPublicRoute()) {
    return null;
  }
  
  if (USE_EMERGENT_AUTH) {
    return await emergentAuth.getCurrentUser(API_URL);
  }
  
  return await supabaseAuth.getCurrentUser(API_URL);
}

/**
 * Check if user is authenticated
 * @returns {Promise<boolean>}
 */
export async function isAuthenticated() {
  if (USE_EMERGENT_AUTH) {
    return await emergentAuth.isAuthenticated(API_URL);
  }
  
  return await supabaseAuth.isAuthenticated();
}

/**
 * Logout user
 * Clears both provider session and backend session
 */
export async function logout() {
  console.log(`[Auth] logout - clearing all auth sessions`);
  
  // P0 FIX: Always clear BOTH providers to prevent stale session bleed.
  // Previously, only the "active" provider was cleared on logout.
  try {
    await supabaseAuth.logout(API_URL);
  } catch (e) {
    console.warn('[Auth] Supabase logout failed:', e);
  }
  
  try {
    await emergentAuth.logout(API_URL);
  } catch (e) {
    console.warn('[Auth] Emergent logout failed:', e);
  }
}

/**
 * Require authentication - redirect to login if not authenticated
 * Use this in protected routes/components
 * @param {function} navigate - React Router navigate function
 * @returns {Promise<object|null>} User if authenticated, null if redirecting
 */
export async function requireAuth(navigate) {
  const user = await getCurrentUser();
  
  if (!user) {
    navigate('/login', { replace: true });
    return null;
  }
  
  return user;
}

/**
 * Subscribe to auth state changes (Supabase only)
 * For Emergent, this is a no-op since state is cookie-based
 * @param {function} callback - Callback for auth state changes
 * @returns {function} Unsubscribe function
 */
export function onAuthStateChange(callback) {
  if (USE_EMERGENT_AUTH) {
    // Emergent auth uses cookies, no client-side subscription
    return () => {};
  }
  
  return supabaseAuth.onAuthStateChange(callback);
}

/**
 * Get auth provider name (for debugging/UI)
 * @returns {string}
 */
export function getAuthProvider() {
  return USE_EMERGENT_AUTH ? 'emergent' : 'supabase';
}
